import {Component, OnInit, OnDestroy} from '@angular/core';
import {BaseComponent} from '../base.component';
import {NotificationService} from '../../../../../common/src/lib/services/notification.service';
import {SendAppNotificationService} from '../../services/api/send-app-notification.service';
import {FirebaseNotificationTaskProgressReport} from '../../dtos/firebaseNotificationTaskProgressReport';
import {SendAppNotificationInitiateDto} from '../../dtos/sendAppNotificationInitiateDto';
import {FirebaseNotificationSendRequirementsDto} from '../../dtos/firebaseNotificationSendRequirementsDto';
import {interval, Subscription} from 'rxjs';
import {switchMap, startWith} from 'rxjs/operators';

@Component({
    selector: 'app-import-csv-patients',
    templateUrl: './send-app-notifications.component.html',
    styleUrls: ['./send-app-notifications.component.scss']
})

export class SendAppNotificationsComponent extends BaseComponent implements OnInit, OnDestroy {

    // New state fields for progress panel
    public progressReport: FirebaseNotificationTaskProgressReport | null = null;
    public progressLoading = false;
    public progressError: string | null = null;

    // New form fields requested by the user
    public title = '';
    public description = '';
    public url = '';
    public imageUrl = '';

    // Specific emails: we keep both raw input and parsed list
    public specificEmailsRaw = '';
    public specificEmails: string[] = [];

    // Language: one of 'EN'|'ES'|'FR' or empty string
    public language = '';

    // New fields for rerun/force-run behavior
    public taskCode = '';
    public forceRun = false;

    // Confirmation modal state
    public showConfirm = false;

    // Result dialog state
    public showResultDialog = false;
    public resultIsSuccess = false;
    public resultStatus = '';
    public resultMessage = '';

    // Polling subscription for progress updates
    private pollingSub: Subscription | null = null;

    constructor(private sendAppNotificationService: SendAppNotificationService,
                private notificationService: NotificationService) {
        super();
    }

    ngOnInit(): void {
        this.analyticsSwitchPage('Admin-ImportCSVPatients', '', '/admin/importCSVPatients');
        this.loadProgress();
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    private loadProgress(): void {
        this.progressLoading = true;
        this.progressError = null;
        this.sendAppNotificationService.progress().subscribe(
            (report: FirebaseNotificationTaskProgressReport) => {
                this.progressReport = report;
                this.progressLoading = false;
            },
            (err: any) => {
                // extract a friendly message
                this.progressError = err && err.error && err.error.message ? err.error.message : (err && err.message ? err.message : 'Could not load notification progress');
                this.progressReport = null;
                this.progressLoading = false;
                try {
                    this.notificationService.info(this.progressError);
                } catch (e) {
                    // ignore notification errors
                }
            }
        );
    }

    public onRefreshProgress(): void {
        this.loadProgress();
    }

    // Toggle language selection: select if different, clear if same
    public toggleLanguage(lang: string): void {
        if (this.language === lang) {
            this.language = '';
        } else {
            this.language = lang;
        }
    }

    // Parse raw specific emails input into a string array (split by comma or newline)
    public onSpecificEmailsRawChange(value: string): void {
        this.specificEmailsRaw = value || '';
        this.specificEmails = this.specificEmailsRaw
            .split(/[,\n]+/) // split on comma or newline
            .map(s => s.trim())
            .filter(Boolean);
    }

    // Whether the Send notification button should be enabled
    public canSend(): boolean {
        const requiredFilled = !!(this.title && this.description && this.url && this.imageUrl);
        const hasTarget = (this.specificEmails && this.specificEmails.length > 0) || !!this.language;
        return requiredFilled && hasTarget;
    }

    // Open confirmation modal
    public onSendClick(): void {
        if (!this.canSend()) {
            return;
        }
        // clear any previous result dialog
        this.showResultDialog = false;
        this.resultIsSuccess = false;
        this.resultStatus = '';
        this.resultMessage = '';
        this.showConfirm = true;
    }

    public cancelConfirm(): void {
        this.showConfirm = false;
    }

    // Confirm send - call backend initiate, update report, then poll progress every 10s until COMPLETED
    public confirmSend(): void {
        // build requirements dto
        const requirements = new FirebaseNotificationSendRequirementsDto(this.language || '');
        const initiateDto = new SendAppNotificationInitiateDto(this.taskCode || '',
            this.title, this.description, this.url, this.imageUrl, requirements, this.forceRun, this.specificEmails);

        // clear any previous result dialog
        this.showResultDialog = false;
        this.resultIsSuccess = false;
        this.resultStatus = '';
        this.resultMessage = '';

        this.sendAppNotificationService.initiate(initiateDto).subscribe(
            (report: FirebaseNotificationTaskProgressReport) => {
                // update report and close dialog
                this.progressReport = report;
                this.showConfirm = false;
                try {
                    this.notificationService.info('Notification initiated. Polling progress...');
                } catch (e) { }

                // start polling every 10 seconds (including immediate)
                this.startPolling();
            },
            (err: any) => {
                const msg = err && err.error && err.error.message ? err.error.message : (err && err.message ? err.message : 'Could not initiate notification');
                try {
                    this.notificationService.info(msg);
                } catch (e) { }
            }
        );
    }

    private startPolling(): void {
        this.stopPolling();
        this.pollingSub = interval(5000).pipe(
            startWith(0),
            switchMap(() => this.sendAppNotificationService.progress())
        ).subscribe(
            (report: FirebaseNotificationTaskProgressReport) => {
                this.progressReport = report;
                if (report && report.status) {
                    const status = report.status;
                    const terminalStatuses = ['COMPLETED', 'FAILED_ACCOUNT_FETCH', 'FAILED_DEVICE_FETCH', 'FAILED_GOOGLE_SEND'];
                    if (terminalStatuses.includes(status)) {
                        // Stop polling and show result dialog
                        this.stopPolling();
                        this.resultStatus = status;
                        if (status === 'COMPLETED') {
                            this.resultIsSuccess = true;
                            this.resultMessage = 'Notification process completed successfully.';
                        } else {
                            this.resultIsSuccess = false;
                            this.resultMessage = report.errorMessage || 'An error occurred during notification processing.';
                        }
                        this.showResultDialog = true;
                    }
                }
            },
            (err: any) => {
                const msg = err && err.error && err.error.message ? err.error.message : (err && err.message ? err.message : 'Could not load notification progress');
                try { this.notificationService.info(msg); } catch (e) {}
                // keep trying on next interval; do not stop polling automatically here
            }
        );
    }

    // Stop and clean up polling subscription if present
    private stopPolling(): void {
        if (this.pollingSub) {
            try {
                this.pollingSub.unsubscribe();
            } catch (e) {
                // ignore unsubscribe errors
            }
            this.pollingSub = null;
        }
    }

    public dismissResult(): void {
        this.showResultDialog = false;
    }
}
